import {
  extractObjectsByRule,
  rulesProperties,
  mapToExcelIndexes,
  addColumnHeader,
} from "../helpers";
import { removeRowsByCondition } from "./removeRows";
import { replaceCellData } from "./replaceCellData";
import { convertDateToSpecifiedDateFormat } from "./convertDateToSpecifiedDateFormat";
import { makeDataColumnAbsolute } from "./makeDataColumnAbsolute";
import { makeDataColumnInteger } from "../functions/makeDataColumnInteger";
import { makeColumnDataNegative } from "./negateColumnData";
import { insertUserDefinedDataInSheet } from "./userDefined";
import { insertCloneColumnDataInSheet } from "./clone";
import { createExecutionSummary, validateInputColumns } from "../utils";
import { fixDecimalPlacesInSheet } from "./fixDecimals";
export const readRulesAndExecuteOnFile = (
  worksheet,
  outputSheet,
  rules,
  inputColumns,
  outputColumns,
  fileSettings,
  outputFileSettings,
  fileName,
) => {
  let summary = createExecutionSummary();

  summary.fileName = fileName;
  summary.startTime = new Date().toISOString();

  // Step 1: Validate input columns
  summary.inputColumnValidation = validateInputColumns(
    worksheet,
    inputColumns,
    fileSettings?.headerRow || 1,
  );

  // Show validation warnings if any
  if (summary.inputValidation && summary.inputValidation.warnings.length > 0) {
    summary.inputValidation.warnings.forEach((warning) => {
      summary.warnings.push(warning);
    });
  }

  inputColumns.forEach((obj) => {
    if (obj.replaceColumnData) {
      const changes = replaceCellData(worksheet, obj);
      summary.InputColumnProcessing.replacements.push({
        column: obj.inColChr,
        changesApplied: changes.length,
        details: changes,
      });
      summary.InputColumnProcessing.totalChanges += changes.length;
    }

    // convert date to specified format
    if (obj.toDateFormat) {
      const changes = convertDateToSpecifiedDateFormat(worksheet, obj);
      const successfulChanges = changes.filter((c) => !c.error);
      const failedChanges = changes.filter((c) => c.error);

      summary.InputColumnProcessing.dateConversions.push({
        column: obj.inColChr,
        totalProcessed: changes.length,
        successful: successfulChanges.length,
        failed: failedChanges.length,
        inputFormat: obj.inputDateFormat,
        outputFormat: obj.toDateFormat,
        details: changes,
      });
    }

    // Make column data absolute
    if (obj.makeColumnAbsolute) {
      const changes = makeDataColumnAbsolute(worksheet, obj);
      summary.InputColumnProcessing.absoluteConversions.push({
        column: obj.inColChr,
        totalProcessed: changes.length,
        details: changes,
      });
      summary.InputColumnProcessing.totalChanges += changes.length;
    }

    // remove dollar sign and convert to integer
    if (obj.makeItInteger) {
      const changes = makeDataColumnInteger(worksheet, obj);
      summary.InputColumnProcessing.integerConversions.push({
        column: obj.inColChr,
        totalProcessed: changes.length,
        details: changes,
      });
      summary.InputColumnProcessing.totalChanges += changes.length;
    }

    // Negate column data
    if (obj.negateColumnData) {
      const changes = makeColumnDataNegative(worksheet, obj);
      summary.InputColumnProcessing.negateConversions.push({
        column: obj.inColChr,
        totalProcessed: changes.length,
        details: changes,
      });
      summary.InputColumnProcessing.totalChanges += changes.length;
    }
  });
  if (fileSettings?.sortColumn?.toggleSortButton) {
    const headerRow = fileSettings?.headerRow || 1;
    const column = fileSettings?.sortColumn?.sortColumnName;
    const order = fileSettings?.sortColumn?.order;
    const columnDataType = fileSettings?.sortColumn?.sortColumnType;

    const data = worksheet.getSheetValues();

    // Get index from column name
    const sortColumnIndex = mapToExcelIndexes(column);
    const sortedData = [...data].sort((a, b) => {
      let valA, valB;
      if (order === "asc") {
        valA = a[sortColumnIndex] || ""; // Default empty string if missing
        valB = b[sortColumnIndex] || "";
      } else {
        // Descending
        valA = b[sortColumnIndex] || "";
        valB = a[sortColumnIndex] || "";
      }
      // Convert to Date if sorting a date column
      if (columnDataType === "date") {
        return new Date(valA) - new Date(valB);
      }
      return valA.toString().localeCompare(valB.toString()); // Default text sorting
    });

    sortedData.forEach((row, rowIndex) => {
      worksheet.getRow(rowIndex + headerRow).values = row; // write file excluding header
    });
  }

  let { objectsWithEntireRowSkipCondition, modifiedOriginal } =
    extractObjectsByRule(rules);

  objectsWithEntireRowSkipCondition.forEach((cond) => {
    const removedRows = removeRowsByCondition(worksheet, cond);
    summary.rowRemoval.operations.push({
      column: cond.inputColumn,
      condition: cond.skipCondition,
      rows: removedRows,
    });
    summary.rowRemoval.totalRowsRemoved += removedRows.length;
  });

  // printWorksheetAsTable(worksheet); // TEMP DEBUG

  for (let i = 0; i < modifiedOriginal.length; i++) {
    let rule = modifiedOriginal[i];

    let result = rulesProperties[rule.appliedRule].function(
      worksheet,
      outputSheet,
      modifiedOriginal,
      rule,
      fileSettings,
      inputColumns,
      outputColumns,
    );

    if (result !== undefined && result !== null) {
      modifiedOriginal[i] = {
        ...rule,
        output: result.data,
      };
    }
    summary.rulesExecution.executedRules.push({
      ruleName: rulesProperties[rule.appliedRule].ruleName,
      ruleId: rule.appliedRule,
      status: "success",
      details: result?.trackingInfo || {},
    });

    summary.rulesExecution.totalRules++;
  }

  outputColumns.forEach((rule) => {
    if (rule.userDefinedData !== "") {
      insertUserDefinedDataInSheet(outputSheet, outputColumns, rule);
      summary.OutputColumns.customeDataColumns.push({
        column: rule.outColChr,
        data: rule.userDefinedData,
      });
    } else {
      addColumnHeader(outputSheet, outputColumns, rule.outColChr);
    }
  });

  outputColumns.forEach((rule) => {
    if (rule.cloneColumn !== "") {
      insertCloneColumnDataInSheet(outputSheet, outputColumns, rule);
      summary.OutputColumns.cloneColumns.push({
        column: rule.outColChr,
        sourceColumn: rule.cloneColumn,
      });
    }
  });

  // write a code for decimal conversion
  outputColumns.forEach((rule) => {
    if (
      rule.fixDecimalPlaces &&
      rule.fixDecimalPlaces !== "" &&
      rule.outColDT === "2"
    ) {
      fixDecimalPlacesInSheet(outputSheet, rule);
    }
  });

  const endTime = new Date();
  summary.endTime = endTime.toISOString();
  summary.executionTimeInMs = (endTime - new Date(summary.startTime)).toFixed(
    2,
  );

  return { outputSheet, summary };
};

// TEMP DEBUG: prints the worksheet's current state as an Excel-like table
// with real column letters (A, B, C, ...) and row numbers, for manual inspection.
const excelColumnLetter = (colNumber) => {
  let letter = "";
  while (colNumber > 0) {
    const rem = (colNumber - 1) % 26;
    letter = String.fromCharCode(65 + rem) + letter;
    colNumber = Math.floor((colNumber - 1) / 26);
  }
  return letter;
};

function printWorksheetAsTable(worksheet) {
  const table = {};
  worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    const rowData = {};
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      rowData[excelColumnLetter(colNumber)] = cell.value ?? "";
    });
    table[`Row ${rowNumber}`] = rowData;
  });
  console.table(table);
}
